import React from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import NavbarAr from "@/components/NavbarAr";
import FooterAr from "@/components/FooterAr";
import Marquee from "@/components/Marquee";
import EventName from "@/components/EventName";
import Brand from "@/components/Brand";
import HeroPromoCard from "@/components/HeroPromoCard";
import { IMAGES, LOGO_URL_TIGHT } from "@/lib/brand";
import { CARS, MOTORCYCLES } from "@/data/builds";

// Arabic edition — structural mirror of Landing.jsx (English structural
// reference, PRD §44.4). Content is replaced section by section using
// approved Arabic editorial copy provided by the user (PRD §43.2, §45).
// Implementation Package 01: Homepage introductory statement replaced.
// All other content on this page remains in English until further
// approved Arabic editorial packages are received.

const FEATURED_BUILDS = CARS.map((b) => ({ slug: b.slug, name: b.name }));
const FEATURED_BIKES = MOTORCYCLES.map((b) => ({ slug: b.slug, name: b.name }));

export default function LandingAr() {
    return (
        <div className="min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)]" data-testid="landing-page-ar" dir="rtl">
            <NavbarAr />

            {/* HERO — Structural mirror of the English Homepage Hero
                (PRD §43.1 / §44.4). Only the introductory statement is
                replaced in Implementation Package 01. All layout,
                typography hierarchy, spacing, styling, and visual
                composition are preserved exactly as in Landing.jsx. */}
            <section className="relative min-h-[calc(100vh-6rem)] flex items-end overflow-hidden border-b border-white/5">
                {/* Structural anchor container (see Landing.jsx for
                    full rationale). AR-mirror not required — the
                    promo is right-anchored in both locales per owner
                    parity spec. */}
                <div className="absolute inset-0 pointer-events-none z-10">
                    <div className="relative mx-auto h-full w-full max-w-[62rem] px-4 md:px-0">
                        <HeroPromoCard locale="ar" />
                    </div>
                </div>
                <div className="absolute inset-0 overflow-hidden">
                    <picture>
                        {/* Mobile-optimized variant — see Landing.jsx
                            for full rationale. */}
                        <source
                            media="(max-width: 767px)"
                            srcSet="/hero-mobile.jpg"
                        />
                        <img
                            src={IMAGES.hero}
                            alt="CrewZNatioN"
                            width={6240}
                            height={3512}
                            loading="eager"
                            decoding="async"
                            fetchpriority="high"
                            className="absolute top-0 h-full object-cover left-0 md:left-[-28%] w-full md:w-[134%]"
                            style={{ maxWidth: "none" }}
                        />
                    </picture>
                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--crewz-bg)]/95 via-[rgba(17,17,17,0.25)] to-[rgba(17,17,17,0.1)]" />
                    <div className="absolute inset-0 bg-gradient-to-l from-[rgba(17,17,17,0.6)] via-transparent to-transparent" />

                    <div
                        className="absolute inset-0 pointer-events-none"
                        aria-hidden="true"
                        style={{
                            background:
                                "linear-gradient(to top, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0.4) 30%, rgba(0,0,0,0.2) 55%, rgba(0,0,0,0.06) 75%, transparent 90%)",
                        }}
                    />
                </div>

                <div className="relative w-full px-6 md:px-12 lg:px-20 pb-1 md:pb-1">
                    <div
                        className="mx-auto max-w-2xl md:max-w-[62rem] flex flex-col items-center text-center translate-y-1 md:translate-y-4"
                    >
                        <h1
                            className="w-full max-w-[7rem] sm:max-w-[7.75rem] md:max-w-[8.75rem] lg:max-w-[10rem] xl:max-w-[11rem] mx-auto hero-wordmark"
                            data-testid="hero-title-ar"
                        >
                            <img
                                src={LOGO_URL_TIGHT}
                                alt="CrewZNatioN"
                                className="w-full h-auto block select-none"
                                draggable="false"
                            />
                        </h1>
                        <p className="font-arabic text-[16px] md:text-[22px] font-normal text-[var(--crewz-text)]/90 max-w-xl md:max-w-none mt-3 md:mt-4 px-5 md:px-0 leading-[1.4] md:leading-[1.35] hero-tagline" data-testid="hero-tagline-ar">
                            من الرياض إلى العالم، تجمع <Brand /> عشاق السيارات والدراجات النارية في مجتمع واحد.
                            <br />
                            وهنا… تتحول المعرفة إلى صداقات، والطلعات إلى ذكريات.
                        </p>
                        <p
                            className="font-display text-xl md:text-2xl lg:text-3xl text-[var(--crewz-text)]/90 leading-none whitespace-nowrap mt-3 md:mt-4 md:-translate-y-1"
                            data-testid="hero-smiles-per-liter-ar"
                            dir="ltr"
                        >
                            Smiles Per Liter
                        </p>
                        <a
                            href="#institutional-presence"
                            aria-label="Continue"
                            className="mt-1 md:mt-2 text-[var(--crewz-gold)] -translate-y-1 md:-translate-y-3"
                            data-testid="hero-continuation-cue-ar"
                        >
                            <ChevronDown size={20} strokeWidth={2.25} />
                        </a>
                    </div>
                </div>

                {/* Continuation cue lives inside the hero group above,
                    directly beneath SMILES PER LITER on the pavement. */}
            </section>

            {/* SECTION 1 — INSTITUTIONAL PRESENCE (English placeholder; awaits Arabic editorial package) */}
            <section
                id="institutional-presence"
                className="bg-[var(--crewz-bg)] border-b border-white/5"
                data-testid="section-institutional-presence-ar"
            >
                <div className="px-6 md:px-12 lg:px-20 py-28 md:py-40 max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
                    <div className="md:col-span-4">
                        <div className="flex items-center justify-end gap-3 mb-6">
                            <span className="gold-rule" />
                            <p
                                className="overline overline-primary"
                                style={{
                                    fontFamily: '"IBM Plex Sans Arabic", Inter, system-ui, sans-serif',
                                }}
                            >
                                المؤسسة
                            </p>
                        </div>
                    </div>
                    <div className="md:col-span-8">
                        <h2
                            className="font-arabic-display text-[var(--crewz-text)]"
                            style={{
                                fontSize: "52px",
                                lineHeight: "58px",
                                fontWeight: 700,
                                letterSpacing: "0em",
                            }}
                        >
                            <span style={{ whiteSpace: "normal" }}>من الرياض… إلى العالم.</span>
                        </h2>
                        <p
                            className="font-arabic text-[var(--crewz-text)]/85 mt-8 max-w-2xl"
                            style={{
                                fontSize: "22px",
                                lineHeight: "36.67px",
                                fontWeight: 400,
                            }}
                        >
                            تنطلق كروزنيشن من الرياض، المملكة العربية السعودية، بحضورٍ إقليمي ورؤيةٍ عالمية. وُجدت للحفاظ على الثقافة المحيطة بالسيارات والدراجات النارية الاستثنائية وتطويرها، وربط أفرادها بمجتمع يجمعهم بين الشغف والمسؤولية.
                        </p>
                    </div>
                </div>
            </section>

            {/* SECTION 2 — THE COMMUNITY AND ITS CHARACTER (Arabic editorial applied Feb 2026) */}
            <section
                className="section-light border-y border-black/5"
                data-testid="section-community-ar"
            >
                <div className="px-6 md:px-12 lg:px-20 py-28 md:py-40 max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
                    <div className="md:col-span-4">
                        <div className="flex items-center gap-3 mb-2">
                            <span className="gold-rule" />
                            <p
                                className="overline overline-primary"
                                style={{
                                    fontFamily: '"IBM Plex Sans Arabic", Inter, system-ui, sans-serif',
                                }}
                            >
                                المجتمع
                            </p>
                        </div>
                        <h2
                            className="font-arabic-display text-[var(--crewz-text-dark)]"
                            style={{
                                fontSize: "42px",
                                lineHeight: "48px",
                                fontWeight: 700,
                                letterSpacing: "0em",
                            }}
                        >
                            نبني المجتمع. وتجمعنا المبادئ.
                        </h2>
                    </div>
                    <div className="md:col-span-8 space-y-6">
                        <p
                            className="font-arabic text-[var(--crewz-text-dark)]/85 max-w-2xl"
                            style={{
                                fontSize: "20px",
                                lineHeight: "33.3px",
                                fontWeight: 400,
                            }}
                        >
                            تقوم كروزنيشن على أشخاص يجمعهم شغفٌ حقيقي بالسيارات والدراجات النارية الاستثنائية، وبالثقافة التي تدور حولها. فالمركبات هي نقطة البداية، أما الإنسان فهو جوهر هذه التجربة.
                        </p>
                        <p
                            className="font-arabic text-[var(--crewz-text-dark)]/85 max-w-2xl"
                            style={{
                                fontSize: "20px",
                                lineHeight: "33.3px",
                                fontWeight: 400,
                            }}
                        >
                            ويقوم هذا المجتمع على الشغف المسؤول؛ حيث يجتمع الشغف مع المسؤولية، والمعرفة مع المشاركة، والاحترام المتبادل، والحرص على الحفاظ على هذه الثقافة واستمرارها.
                        </p>
                    </div>
                </div>
            </section>

            {/* SECTION 3 — THE COMMUNITY IN MOTION (Arabic editorial applied Feb 2026) */}
            <section
                className="bg-[var(--crewz-bg)]"
                data-testid="section-activity-ar"
            >
                <div className="px-6 md:px-12 lg:px-20 pt-28 md:pt-40 pb-16 max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
                    <div className="md:col-span-4">
                        <div className="flex items-center justify-end gap-3 mb-6 md:-translate-x-8">
                            <span className="gold-rule" />
                            <p
                                className="overline overline-primary"
                                style={{
                                    fontFamily: '"IBM Plex Sans Arabic", Inter, system-ui, sans-serif',
                                }}
                            >
                                المجتمع في حركة دائمة
                            </p>
                        </div>
                    </div>
                    <div className="md:col-span-8">
                        <h2
                            className="font-arabic-display text-[var(--crewz-text)]"
                            style={{
                                fontSize: "52px",
                                lineHeight: "58px",
                                fontWeight: 700,
                                letterSpacing: "0em",
                            }}
                        >
                            من الطريق… إلى <span className="!text-[40px] md:!text-[52px]" style={{ whiteSpace: "nowrap" }}><EventName /></span>
                        </h2>
                        <p
                            className="font-arabic text-[var(--crewz-text)]/85 mt-8 max-w-2xl"
                            style={{
                                fontSize: "22px",
                                lineHeight: "36.67px",
                                fontWeight: 400,
                            }}
                        >
                            تنبض كروزنيشن بالحياة من خلال أفرادها؛ في الطلعات، واللقاءات، وفي <span style={{ whiteSpace: "nowrap" }}><EventName /></span> في كل لقاء تتكوّن صداقات جديدة، وتتحول لحظات الشغف إلى ذكريات تدوم. هكذا تتحرك كروزنيشن: مجتمع واحد يجمعه الشغف وتضبطه المسؤولية.
                        </p>
                    </div>
                </div>

                <div className="mt-12">
                    <div className="px-6 md:px-12 lg:px-20 max-w-6xl mx-auto">
                        <p
                            className="overline text-[var(--crewz-text)]/55 mb-6 !text-[30px] md:!text-[26px] translate-x-[calc(346px_-_100vw)] md:translate-x-[-26px]"
                            style={{
                                fontFamily: '"IBM Plex Sans Arabic", Inter, system-ui, sans-serif',
                                display: "flex",
                                direction: "rtl",
                                justifyContent: "flex-start",
                                alignItems: "baseline",
                                width: "100%",
                                whiteSpace: "nowrap",
                                gap: "88.8515625px",
                            }}
                        >
                            <span style={{ whiteSpace: "nowrap" }}>سياراتنا.</span>
                            <span style={{ whiteSpace: "nowrap" }}>دراجاتنا.</span>
                            <span style={{ whiteSpace: "nowrap" }}>مجتمعنا.</span>
                        </p>
                    </div>
                    <div className="bg-[var(--crewz-bg-2)] border-y border-white/5" data-testid="featured-marquee-ar">
                        <Marquee items={FEATURED_BUILDS} speed={38} testid="featured-cars-marquee-ar" />
                        <Marquee items={FEATURED_BIKES} speed={42} reverse testid="featured-bikes-marquee-ar" />
                    </div>
                </div>

                <div className="px-6 md:px-12 lg:px-20 pt-20 pb-28 md:pb-32 max-w-6xl mx-auto">
                    <div className="flex items-center gap-3 mb-10">
                        <span className="gold-rule" />
                        <p className="overline overline-primary">نجمع المجتمع حول شغفٍ واحد.</p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10 items-start">
                        <div className="text-center" data-testid="stat-participating-vehicles-ar">
                            <div className="font-display text-4xl md:text-5xl text-[var(--crewz-text)] leading-none h-9 md:h-12 flex items-end justify-center"><span dir="ltr">1,100+</span></div>
                            <p className="mt-5 text-[15.5px] md:text-[17.5px] text-[var(--crewz-text)]/70 leading-relaxed font-arabic">
                                سيارة ودراجة نارية مشاركة
                            </p>
                        </div>
                        <div className="text-center" data-testid="stat-visitors-ar">
                            <div className="font-display text-4xl md:text-5xl text-[var(--crewz-text)] leading-none h-9 md:h-12 flex items-end justify-center"><span dir="ltr">17,000+</span></div>
                            <p className="mt-5 text-[15.5px] md:text-[17.5px] text-[var(--crewz-text)]/70 leading-relaxed font-arabic">
                                زائر خلال ما يقارب تسعت ساعات
                            </p>
                        </div>
                        <div className="text-center" data-testid="stat-home-ar">
                            <div className="font-arabic-display text-[1.9rem] md:text-[2.5rem] text-[var(--crewz-text)] leading-none h-9 md:h-12 flex items-end justify-center">الرياض</div>
                            <p className="mt-5 text-[15.5px] md:text-[17.5px] text-[var(--crewz-text)]/70 leading-relaxed font-arabic">
                                موطننا
                            </p>
                        </div>
                        <div className="text-center" data-testid="stat-horizon-ar">
                            <div className="font-arabic-display text-[1.9rem] md:text-[2.5rem] text-[var(--crewz-text)] leading-none whitespace-nowrap h-9 md:h-12 flex items-end justify-center">
                                إقليمي ودولي
                            </div>
                            <p className="mt-5 text-[15.5px] md:text-[17.5px] text-[var(--crewz-text)]/70 leading-relaxed font-arabic">
                                امتدادنا
                            </p>
                        </div>
                    </div>
                    <p className="mt-10 text-[20px] md:text-[22px] text-[var(--crewz-text)]/70 text-center font-medium font-arabic leading-relaxed">
                        تعود هذه الأرقام إلى النسخة الأولى من <EventName />
                    </p>
                </div>
            </section>

            {/* SECTION 4 — THE GATHERING AS FLAGSHIP EXPRESSION (Arabic)
                Structural reset: exact RTL mirror of the English section.
                Same wrapper, container width, padding, grid, spans, gap, and
                top alignment as the English `THE FLAGSHIP EXPRESSION / THE
                GATHERING.` section. Only direction (rtl on page root) +
                language-specific font classes differ. */}
            <section
                className="bg-[var(--crewz-bg-2)] border-t border-white/5"
                data-testid="section-gathering-ar"
            >
                <div className="px-6 md:px-12 lg:px-20 py-28 md:py-40 max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
                    <div className="md:col-span-4">
                        <div className="flex items-center gap-3 mb-6 md:-translate-x-12">
                            <span className="gold-rule" />
                            <p
                                className="overline overline-primary"
                                style={{
                                    fontFamily: '"IBM Plex Sans Arabic", Inter, system-ui, sans-serif',
                                    textTransform: "none",
                                }}
                            >
                                الفعالية الأبرز
                            </p>
                        </div>
                        <h2 className="mt-2 md:mt-3 text-[36.9px] md:text-[46.2px] lg:text-[55.4px] leading-[0.98] tracking-[-0.015em] text-[var(--crewz-text)] md:translate-x-[28px]" style={{ fontFamily: '"Alexandria", "Inter", system-ui, sans-serif', fontWeight: 700 }}>
                            <EventName variant="stacked" />
                        </h2>
                    </div>
                    <div className="md:col-span-8">
                        <p
                            className="font-arabic text-[var(--crewz-text)]/85 max-w-2xl"
                            style={{
                                fontSize: "26px",
                                lineHeight: "44.2px",
                                fontWeight: 400,
                            }}
                        >
                            <EventName /> هو أحد أبرز فعاليات <Brand />، حيث يلتقي فيه عشاق السيارات والدراجات النارية في تجربة تعكس الشغف، وتجمع أفراد المجتمع تحت مظلة <Brand />
                        </p>

                        <Link
                            to="/ar/gathering"
                            className="group mt-10 inline-flex items-baseline gap-3 font-arabic-display text-xl md:text-2xl text-[var(--crewz-text)]/90 tracking-[0.01em] hover:text-[var(--crewz-gold)] transition-colors duration-300"
                            data-testid="gathering-cta-ar"
                        >
                            <span className="border-b border-transparent group-hover:border-[var(--crewz-gold)]/60 transition-colors duration-300 pb-0.5">
                                ١–٣ أكتوبر ٢٠٢٦، الرمال سنتر، الرياض
                            </span>
                            <ArrowRight size={18} strokeWidth={1.5} className="translate-y-0.5 group-hover:-translate-x-1 transition-transform duration-300 rtl-flip" />
                        </Link>
                    </div>
                </div>
            </section>

            <FooterAr />
        </div>
    );
}
